import { addDays } from "date-fns"
import { sum } from "lodash"
import { useMemo, useState } from "react"

import { Button, IconButton } from "~/components/reusable/Button"
import { Card, CardContent, CardTitle } from "~/components/reusable/Card"
import {
  DialogBodyV2,
  DialogContentV2,
  DialogHeaderV2,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "~/components/reusable/Dialog"
import { Popover, PopoverContent, PopoverTrigger } from "~/components/reusable/Popover"
import { Tooltip } from "~/components/reusable/Tooltip"
import { useStockOutTimesV2 } from "~/hooks/useStockOutTimesV2"
import { useVariants } from "~/hooks/useVariants"
import { startOfTodayUTC } from "~/utils/datetime"
import { filterOnSearchString } from "~/utils/search"
import { Image } from "~/components/reusable/Image"
import { Plus } from "@phosphor-icons/react"
import { roundToColliSize } from "../CreateOrUpdatePurchaseOrder"
import SearchField from "~/components/planning/SearchField"

type IItem = { skuId: string; warehouseId: string; quantity: number }

type Props = {
  alreadySelectedSkus: string[]
  addSkus: (skus: IItem[], expectedDate: Date, endOfPeriod: Date) => void
  selectedVendor: string | undefined
  selectedWarehouse: string | undefined
  addedDemand: string[]
  startDate: Date
  endDate: Date
}

export default function AddVariantsToPurchaseOrderDialog({
  alreadySelectedSkus,
  addSkus,
  selectedVendor,
  selectedWarehouse,
  addedDemand,
  startDate,
  endDate,
}: Props) {
  const [open, setOpen] = useState(false)
  const hasVendorAndWarehouse = selectedVendor && selectedWarehouse

  return (
    <DialogRoot open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <IconButton variant="neutral" size="sm" disabled={!hasVendorAndWarehouse}>
          <Tooltip
            className={!hasVendorAndWarehouse ? "border-warning border-2 mt-5" : ""}
            body={
              !hasVendorAndWarehouse ? (
                <div>
                  <div className="font-bold pb-2">Add variants to purchase order</div>
                  <div className="flex flex-col gap-2">
                    <p className="text-md">
                      You need to select a warehouse and a vendor to see suggested variants.
                    </p>
                  </div>
                </div>
              ) : (
                "Add variants to purchase order"
              )
            }
            side="bottom"
            keepOpen={!hasVendorAndWarehouse}
          >
            <Plus />
          </Tooltip>
        </IconButton>
      </DialogTrigger>
      {open && (
        <DialogContentV2 className="flex flex-col gap-2 mb-1 min-w-[800px]">
          <AddVariantsToPurchaseOrderDialogContent
            alreadySelectedSkus={alreadySelectedSkus}
            addSkus={addSkus}
            selectedVendor={selectedVendor}
            selectedWarehouse={selectedWarehouse}
            addedDemand={addedDemand}
            startDate={startDate}
            endDate={endDate}
          />
        </DialogContentV2>
      )}
    </DialogRoot>
  )
}

function AddVariantsToPurchaseOrderDialogContent({
  alreadySelectedSkus,
  addSkus,
  selectedVendor,
  selectedWarehouse,
  addedDemand,
  startDate,
  endDate,
}: Props) {
  const [searchText, setSearchText] = useState("")

  const rawVariants = useVariants()

  const filteredVariants = useMemo(() => {
    const selectedSkuIds = new Set(alreadySelectedSkus)
    return filterOnSearchString(rawVariants, searchText)
      .filter((s) => (selectedVendor ? s.vendors.some((v) => v.id === selectedVendor) : true))
      .filter((s) => !s.skuIds.some((skuId) => selectedSkuIds.has(skuId)))
  }, [searchText, selectedVendor, alreadySelectedSkus, rawVariants])

  const variantIds = useMemo(() => filteredVariants?.map((v) => v.variantId), [filteredVariants])
  const stockoutTimes = useStockOutTimesV2(variantIds, startDate, endDate)

  const getSuggestedOrderForSkuIds = (skuIds: string[]) => {
    const suggestions = skuIds.map((skuId) => {
      const stockoutDetails = stockoutTimes?.get(skuId)?.details
      let suggestedOrder = 0

      if (stockoutDetails) {
        Object.entries(stockoutDetails).forEach(([warehouseId, details]) => {
          if (selectedWarehouse !== warehouseId && !addedDemand.includes(warehouseId)) return

          suggestedOrder += details.suggestedOrder ?? 0
        })
      }
      return suggestedOrder
    })

    return sum(suggestions)
  }

  const variants = filteredVariants
    .map((variant) => {
      const totalSuggestedOrder = getSuggestedOrderForSkuIds(variant.skuIds)

      return {
        variantId: variant.variantId,
        productName: variant.productName,
        variantName: variant.variantName,
        suggestedOrder: totalSuggestedOrder,
        imageUrl: variant.imageUrl,
        items: variant.skuIds.map((skuId) => {
          const suggestedOrder = getSuggestedOrderForSkuIds([skuId])
          return {
            skuId,
            warehouseId: selectedWarehouse ?? "",
            quantity: suggestedOrder
              ? roundToColliSize(suggestedOrder, variant.settings.colliSize)
              : 0,
          }
        }),
        expectedArrivalDate: addDays(startOfTodayUTC(), variant.settings.totalLeadTimeDays ?? 0),
        nextOrderArrivalDate: addDays(
          startOfTodayUTC(),
          (variant.settings.totalLeadTimeDays ?? 0) +
            (Math.ceil(variant.settings.purchaseIntervalDays) || 7),
        ),
      }
    })
    .sort((a, b) => b.suggestedOrder - a.suggestedOrder)

  return (
    <>
      <DialogHeaderV2>
        <DialogTitle>Add variants to purchase order</DialogTitle>
      </DialogHeaderV2>
      <DialogBodyV2>
        <SearchField
          initialSearchText={searchText}
          onSearchTextChange={setSearchText}
          placeholder="Search for variants"
          containerClassName="w-[300px]"
        />
        <p className="text-md font-medium">
          {searchText === ""
            ? "Here are some of your top suggested orders based on your current selected vendor:"
            : "Search result:"}
          {variants.length < 10 ? "" : " (10 out of " + variants.length + " variants)"}
        </p>
        <div className="grid grid-cols-2 gap-3 w-full">
          {variants.length > 0 ? (
            variants.slice(0, 10).map((variant) => (
              <Popover key={variant.variantId}>
                <PopoverTrigger>
                  <Card className="flex items-center h-full w-full">
                    <CardContent className="flex items-center gap-3 hover:bg-fg-muted/5 h-full w-full">
                      <Image
                        src={variant.imageUrl ?? ""}
                        alt={variant.productName ?? "No image"}
                        width={60}
                        height={60}
                        className="rounded-md"
                      />
                      <div className="text-left w-full">
                        <CardTitle>
                          {variant.productName} ({variant.variantName})
                        </CardTitle>
                        <p className="text-sm">
                          Suggested order: <b>{variant.suggestedOrder}</b>
                        </p>
                        <p className="text-sm">SKUs: {variant.items.length}</p>
                      </div>
                    </CardContent>
                  </Card>
                </PopoverTrigger>
                <PopoverContent className="p-2 w-fit">
                  <p className="text-sm font-medium mb-1">Select which SKUs you want to add:</p>
                  <div className="flex gap-2">
                    <Button
                      variant="neutral"
                      size="sm"
                      onClick={() =>
                        addSkus(
                          variant.items,
                          variant.expectedArrivalDate,
                          variant.nextOrderArrivalDate,
                        )
                      }
                    >
                      Add all
                    </Button>
                    <Button
                      variant="neutral"
                      size="sm"
                      onClick={() =>
                        addSkus(
                          variant.items.filter((i) => i.quantity !== 0),
                          variant.expectedArrivalDate,
                          variant.nextOrderArrivalDate,
                        )
                      }
                    >
                      Add with suggested order
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            ))
          ) : (
            <p className="text-md">No variants found</p>
          )}
        </div>
      </DialogBodyV2>
    </>
  )
}
